export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

export function v3(x = 0, y = 0, z = 0): Vec3 {
  return { x, y, z };
}
export function copy(a: Vec3): Vec3 {
  return { x: a.x, y: a.y, z: a.z };
}
export function add(a: Vec3, b: Vec3): Vec3 {
  return { x: a.x + b.x, y: a.y + b.y, z: a.z + b.z };
}
export function sub(a: Vec3, b: Vec3): Vec3 {
  return { x: a.x - b.x, y: a.y - b.y, z: a.z - b.z };
}
export function scale(a: Vec3, s: number): Vec3 {
  return { x: a.x * s, y: a.y * s, z: a.z * s };
}
export function dot(a: Vec3, b: Vec3): number {
  return a.x * b.x + a.y * b.y + a.z * b.z;
}
export function cross(a: Vec3, b: Vec3): Vec3 {
  return { x: a.y * b.z - a.z * b.y, y: a.z * b.x - a.x * b.z, z: a.x * b.y - a.y * b.x };
}
export function length(a: Vec3): number {
  return Math.sqrt(a.x * a.x + a.y * a.y + a.z * a.z);
}
export function length2(a: Vec3): number {
  return a.x * a.x + a.y * a.y + a.z * a.z;
}
export function distance(a: Vec3, b: Vec3): number {
  return length(sub(a, b));
}
/** Unit vector, or the zero vector when the input is (near) zero. */
export function normalize(a: Vec3): Vec3 {
  const l = length(a);
  return l > 1e-9 ? scale(a, 1 / l) : v3();
}
export function lerp(a: Vec3, b: Vec3, t: number): Vec3 {
  return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t, z: a.z + (b.z - a.z) * t };
}
/** Move `from` toward `to` by at most `maxStep`. */
export function moveToward(from: Vec3, to: Vec3, maxStep: number): Vec3 {
  const d = sub(to, from);
  const l = length(d);
  if (l <= maxStep || l < 1e-9) return copy(to);
  return add(from, scale(d, maxStep / l));
}
export function clampLength(a: Vec3, max: number): Vec3 {
  const l = length(a);
  return l > max && l > 1e-9 ? scale(a, max / l) : copy(a);
}
/**
 * Fighter-local (x right, y up, z forward) into world space for a body yaw.
 *
 * The world is right-handed with y up, so a body facing `f = (sin y, 0, cos y)`
 * has its right at `cross(f, up) = (-cos y, 0, sin y)`. A "right, up, forward"
 * body frame is therefore left-handed and this change of basis is a reflection,
 * not a rotation. Getting its sign wrong mirrors everything: strafing, the side
 * a limb swings from and the direction the camera turns.
 */
export function rotateY(a: Vec3, sinYaw: number, cosYaw: number): Vec3 {
  return { x: a.z * sinYaw - a.x * cosYaw, y: a.y, z: a.x * sinYaw + a.z * cosYaw };
}
/** Inverse of rotateY. A reflection is its own inverse, so this is the same map. */
export function unrotateY(a: Vec3, sinYaw: number, cosYaw: number): Vec3 {
  return rotateY(a, sinYaw, cosYaw);
}
